import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Switch } from '../../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Alert, AlertDescription } from '../../ui/alert';
import { Plus, Edit, Trash2, Eye, EyeOff, Server, Cpu, HardDrive, Network, Zap, Save, X, CheckCircle, AlertCircle } from 'lucide-react';
import { API_URLS } from '../../../config/api';

const AdminCorporateServerPackages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const toBool = (v) => v === true || v === '1' || v === 1;

  const packageColorOptions = [
    { value: 'from-orange-500 to-red-500', label: 'Turuncu (Orta)', bg: 'bg-orange-50' },
    { value: 'from-purple-500 to-pink-500', label: 'Mor (Orta)', bg: 'bg-purple-50' },
    { value: 'from-green-500 to-emerald-500', label: 'Yeşil (Orta)', bg: 'bg-green-50' },
    { value: 'from-blue-500 to-cyan-500', label: 'Mavi (Orta)', bg: 'bg-blue-50' }
  ];

  const [formData, setFormData] = useState({
    name: '',
    cpu_cores: 10,
    cpu_model: 'Intel® Xeon Silver',
    ram: '32 GB',
    storage: '2 x 480 GB SSD',
    bandwidth: '1 Gbit',
    price: '',
    original_price: '',
    discount: 0,
    color: 'from-orange-500 to-red-500',
    bg_color: 'bg-orange-50',
    is_popular: false,
    features: [
      { name: 'IP/KVM', included: true },
      { name: 'DDoS Koruması', included: true },
      { name: '%99,9 Uptime', included: true }
    ],
    sort_order: 0,
    url: ''
  });

  const normalize = (s) => (s || '').toString().toLowerCase();
  const isCorporate = (pkg) => {
    const n = normalize(pkg?.name);
    const t = normalize(pkg?.type || pkg?.category_name);
    return /kurumsal|xtreme/.test(n) || /kurumsal/.test(t) || (Number(pkg?.cpu_cores) || 0) >= 10;
  };

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_URLS.ADMIN.CORPORATE_SERVER_PACKAGES);
      const result = await response.json();
      if (result.status === 'success') {
        // Features'ların array olduğundan emin ol ve obje formatına çevir
        const all = result.data.map(pkg => {
          let rawFeatures = Array.isArray(pkg.features) ? pkg.features : [];
          const parsedFeatures = rawFeatures.map(f => {
            if (typeof f === 'string') {
              return { name: f, included: true };
            }
            if (f && typeof f === 'object') {
              return { name: f.name || f.text || '', included: f.included !== false };
            }
            return { name: String(f), included: true };
          });
          return { ...pkg, features: parsedFeatures };
        });
        setPackages(all.filter(isCorporate));
      } else {
        setError('Paketler yüklenirken hata oluştu');
      }
    } catch (err) {
      setError('Paketler yüklenirken hata oluştu: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPackages(); }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      cpu_cores: 10,
      cpu_model: 'Intel® Xeon Silver',
      ram: '32 GB',
      storage: '2 x 480 GB SSD',
      bandwidth: '1 Gbit',
      price: '',
      original_price: '',
      discount: 0,
      color: 'from-orange-500 to-red-500',
      bg_color: 'bg-orange-50',
      is_popular: false,
      features: [
      { name: 'IP/KVM', included: true },
      { name: 'DDoS Koruması', included: true },
      { name: '%99,9 Uptime', included: true }
    ],
    sort_order: 0,
    url: ''
  });
    setEditingPackage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const normalizedForm = { ...formData, name: (formData.name || '').replace(/^(VPS|VDS)/i, 'Kurumsal') };

    try {
      const url = editingPackage 
        ? `${API_URLS.ADMIN.UPDATE_CORPORATE_SERVER_PACKAGE}&package_id=${editingPackage.id}`
        : API_URLS.ADMIN.CREATE_CORPORATE_SERVER_PACKAGE;
      const method = editingPackage ? 'PUT' : 'POST';
      const response = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(normalizedForm) });
      const result = await response.json();
      if (result.status === 'success') {
        setIsDialogOpen(false);
        resetForm();
        fetchPackages();
      } else {
        setError(result.message || 'İşlem başarısız');
      }
    } catch (err) {
      setError('İşlem sırasında hata oluştu: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name,
      cpu_cores: pkg.cpu_cores,
      cpu_model: pkg.cpu_model,
      ram: pkg.ram,
      storage: pkg.storage,
      bandwidth: pkg.bandwidth,
      price: pkg.price,
      original_price: pkg.original_price,
      discount: pkg.discount,
      color: pkg.color,
      bg_color: pkg.bg_color,
      is_popular: toBool(pkg.is_popular),
      features: (pkg.features || []).map(f => 
        typeof f === 'object' ? { ...f } : { name: f, included: true }
      ),
      sort_order: pkg.sort_order,
      url: pkg.url || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (packageId) => {
    if (!confirm('Bu paketi silmek istediğinizden emin misiniz?')) return;
    try {
      const response = await fetch(`${API_URLS.ADMIN.DELETE_CORPORATE_SERVER_PACKAGE}&package_id=${packageId}`, { method: 'DELETE' });
      const result = await response.json();
      if (result.status === 'success') { fetchPackages(); } else { setError(result.message || 'Silme işlemi başarısız'); }
    } catch (err) {
      setError('Silme sırasında hata oluştu: ' + err.message);
    }
  };

  const handleToggleStatus = async (packageId) => {
    try {
      const response = await fetch(`${API_URLS.ADMIN.TOGGLE_CORPORATE_SERVER_PACKAGE_STATUS}&package_id=${packageId}`, { method: 'PATCH' });
      const result = await response.json();
      if (result.status === 'success') { fetchPackages(); } else { setError(result.message || 'Durum değiştirme başarısız'); }
    } catch (err) {
      setError('Durum değiştirme sırasında hata oluştu: ' + err.message);
    }
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, { name: '', included: true }]
    }));
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const updateFeature = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.map((feature, i) => 
        i === index ? { ...feature, [field]: value } : feature
      )
    }));
  };

  const toggleFeatureIncluded = (idx) => {
    setFormData((prev) => {
      const updatedFeatures = [...prev.features];
      if (updatedFeatures[idx]) {
        updatedFeatures[idx] = {
          ...updatedFeatures[idx],
          included: !updatedFeatures[idx].included
        };
      }
      return { ...prev, features: updatedFeatures };
    });
  };

  if (loading && packages.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6 overflow-x-hidden">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="w-full sm:w-auto">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">Kurumsal Sunucu Paketleri Yönetimi</h1>
          <p className="text-sm sm:text-base text-gray-600 break-words">/kurumsal-sunucu sayfasındaki paketleri yönetin</p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="w-full sm:w-auto">
              <Plus className="w-4 h-4 mr-2" />Yeni Paket Ekle
            </Button>
          </DialogTrigger>

          <DialogContent className="max-w-[95vw] sm:max-w-md md:max-w-lg lg:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-base sm:text-lg">{editingPackage ? 'Paketi Düzenle' : 'Yeni Paket Ekle'}</DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-sm break-words">{error}</AlertDescription>
                </Alert>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <Label htmlFor="name" className="text-sm">Paket Adı</Label>
                  <Input id="name" value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} placeholder="Xtreme 12 Core" required className="w-full" />
                </div>
                <div>
                  <Label htmlFor="sort_order" className="text-sm">Sıralama</Label>
                  <Input id="sort_order" type="number" value={formData.sort_order} onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) }))} placeholder="0" className="w-full" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <Label htmlFor="cpu_cores" className="text-sm">CPU Çekirdek</Label>
                  <Input id="cpu_cores" type="number" value={formData.cpu_cores} onChange={(e) => setFormData(prev => ({ ...prev, cpu_cores: parseInt(e.target.value) }))} placeholder="10" required className="w-full" />
                </div>
                <div>
                  <Label htmlFor="ram" className="text-sm">RAM</Label>
                  <Input id="ram" value={formData.ram} onChange={(e) => setFormData(prev => ({ ...prev, ram: e.target.value }))} placeholder="32 GB" required className="w-full" />
                </div>
                <div className="sm:col-span-2 lg:col-span-1">
                  <Label htmlFor="storage" className="text-sm">Depolama</Label>
                  <Input id="storage" value={formData.storage} onChange={(e) => setFormData(prev => ({ ...prev, storage: e.target.value }))} placeholder="2 x 480 GB SSD" required className="w-full" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <Label htmlFor="cpu_model" className="text-sm">CPU Modeli</Label>
                  <Input id="cpu_model" value={formData.cpu_model} onChange={(e) => setFormData(prev => ({ ...prev, cpu_model: e.target.value }))} placeholder="Intel® Xeon Silver" required className="w-full" />
                </div>
                <div>
                  <Label htmlFor="bandwidth" className="text-sm">Bant Genişliği</Label>
                  <Input id="bandwidth" value={formData.bandwidth} onChange={(e) => setFormData(prev => ({ ...prev, bandwidth: e.target.value }))} placeholder="1 Gbit" required className="w-full" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <Label htmlFor="price" className="text-sm">Fiyat (TL)</Label>
                  <Input id="price" type="number" step="0.01" value={formData.price} onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) }))} placeholder="3399.00" required className="w-full" />
                </div>
                <div>
                  <Label htmlFor="original_price" className="text-sm">Orijinal Fiyat (TL)</Label>
                  <Input id="original_price" type="number" step="0.01" value={formData.original_price} onChange={(e) => setFormData(prev => ({ ...prev, original_price: parseFloat(e.target.value) }))} placeholder="3899.00" required className="w-full" />
                </div>
                <div className="sm:col-span-2 lg:col-span-1">
                  <Label htmlFor="discount" className="text-sm">İndirim (%)</Label>
                  <Input id="discount" type="number" value={formData.discount} onChange={(e) => setFormData(prev => ({ ...prev, discount: parseInt(e.target.value) }))} placeholder="10" className="w-full" />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch id="is_popular" checked={formData.is_popular} onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_popular: checked }))} />
                <Label htmlFor="is_popular" className="text-sm">Popüler Paket</Label>
              </div>

              <div>
                <Label htmlFor="url" className="text-sm">URL (Paket Sayfası Linki)</Label>
                <Input
                  id="url"
                  type="text"
                  value={formData.url || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="/kurumsal-sunucu veya https://example.com/paket"
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1 break-words">Paket detay sayfası veya satın alma sayfası URL'si</p>
              </div>

              <div>
                <Label className="text-sm">Renk Seçimi</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                  {packageColorOptions.map((color) => (
                    <button key={color.value} type="button" onClick={() => setFormData(prev => ({ ...prev, color: color.value, bg_color: color.bg }))} className={`p-3 rounded-lg border-2 transition-all ${formData.color === color.value ? 'border-orange-500 shadow-lg' : 'border-gray-200 hover:border-gray-300'}`}>
                      <div className={`w-full h-8 rounded bg-gradient-to-r ${color.value} mb-2`}></div>
                      <span className="text-xs text-gray-600">{color.label}</span>
                    </button>
                  ))}
                </div>
                <p className="text-sm text-gray-500 mt-1 break-words">Seçili: {packageColorOptions.find(c => c.value === formData.color)?.label || 'Turuncu (Orta)'}</p>
              </div>

              <div>
                <Label className="text-sm">Özellikler</Label>
                <div className="space-y-2 mt-2">
                  {formData.features.map((feature, index) => {
                    // Güvenli string extraction
                    const featureName = typeof feature === 'string' 
                      ? feature 
                      : (feature?.name || feature?.text || '');
                    const isIncluded = typeof feature === 'object' ? (feature.included !== false) : true;
                    return (
                      <div key={index} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3 border rounded px-3 py-2 bg-gray-50">
                        <div className="flex items-center gap-2 sm:gap-3 flex-1 w-full sm:w-auto">
                          <Switch 
                            checked={isIncluded}
                            onCheckedChange={() => toggleFeatureIncluded(index)}
                            className="data-[state=checked]:bg-green-600 flex-shrink-0"
                          />
                          <Input
                            value={featureName}
                            onChange={(e) => updateFeature(index, 'name', e.target.value)}
                            placeholder="Özellik açıklaması"
                            className={`flex-1 min-w-0 ${isIncluded ? '' : 'text-gray-400'}`}
                          />
                          <span className={`text-xs px-2 py-0.5 rounded whitespace-nowrap flex-shrink-0 ${isIncluded ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {isIncluded ? 'Dahil' : 'Hariç'}
                          </span>
                        </div>
                        <Button type="button" variant="outline" size="icon" onClick={() => removeFeature(index)} className="flex-shrink-0 self-end sm:self-auto">
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                  <Button type="button" variant="outline" onClick={addFeature} className="w-full">
                    <Plus className="w-4 h-4 mr-2" /> Özellik Ekle
                  </Button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:space-x-2 sm:space-y-0">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="w-full sm:w-auto">İptal</Button>
                <Button type="submit" disabled={loading} className="w-full sm:w-auto">
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {editingPackage ? 'Güncelle' : 'Ekle'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-sm break-words">{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid gap-3 sm:gap-4">
        {packages.map((pkg) => (
          <Card key={pkg.id} className="relative overflow-hidden">
            <CardHeader className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start gap-3 sm:gap-0">
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-gradient-to-r ${pkg.color} flex items-center justify-center flex-shrink-0`}>
                    <Server className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <CardTitle className="flex flex-wrap items-center gap-2 text-base sm:text-lg">
                      <span className="break-words">{pkg.name}</span>
                      {toBool(pkg.is_popular) && (
                        <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 text-xs">Popüler</Badge>
                      )}
                      {!pkg.is_active && (<Badge variant="destructive" className="text-xs">Pasif</Badge>)}
                    </CardTitle>
                    <p className="text-xs sm:text-sm text-gray-600 break-words">Sıra: {pkg.sort_order}</p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:space-x-2 w-full sm:w-auto">
                  <Button variant="outline" size="sm" onClick={() => handleToggleStatus(pkg.id)} className="w-full sm:w-auto">
                    {pkg.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleEdit(pkg)} className="w-full sm:w-auto">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(pkg.id)} className="w-full sm:w-auto">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0">
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-4">
                <div className="flex items-center space-x-2">
                  <Cpu className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <span className="text-xs sm:text-sm break-words">{pkg.cpu_cores} Çekirdek</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Zap className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <span className="text-xs sm:text-sm break-words">{pkg.ram}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <HardDrive className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <span className="text-xs sm:text-sm break-words">{pkg.storage}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Network className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <span className="text-xs sm:text-sm break-words">{pkg.bandwidth}</span>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-base sm:text-lg font-bold text-green-600 break-words">{pkg.price} ₺</span>
                  {pkg.original_price > pkg.price && (<span className="text-xs sm:text-sm text-gray-500 line-through break-words">{pkg.original_price} ₺</span>)}
                  {pkg.discount > 0 && (<Badge variant="secondary" className="text-xs">%{pkg.discount} İndirim</Badge>)}
                </div>
                <div className="text-xs sm:text-sm text-gray-600 break-words">{pkg.cpu_model}</div>
              </div>
              {Array.isArray(pkg.features) && pkg.features.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-2 break-words">Özellikler:</h4>
                  <div className="flex flex-wrap gap-1 sm:gap-2">
                    {pkg.features.map((feature, index) => {
                      // Güvenli string extraction
                      const featureName = typeof feature === 'string' 
                        ? feature 
                        : (feature?.name || feature?.text || String(feature));
                      const isIncluded = typeof feature === 'object' ? (feature.included !== false) : true;
                      return (
                        <Badge 
                          key={index} 
                          variant="outline" 
                          className={`text-xs ${isIncluded ? '' : 'bg-gray-100 text-gray-400 line-through'}`}
                        >
                          {isIncluded ? (
                            <CheckCircle className="w-3 h-3 mr-1 text-green-500" />
                          ) : (
                            <X className="w-3 h-3 mr-1 text-gray-400" />
                          )}
                          {featureName}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {packages.length === 0 && !loading && (
        <Card>
          <CardContent className="text-center py-8">
            <Server className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Henüz Kurumsal Sunucu Paketi Yok</h3>
            <p className="text-gray-600 mb-4">İlk paketinizi oluşturmak için yukarıdaki butona tıklayın.</p>
            <Button onClick={() => { resetForm(); setIsDialogOpen(true); }}>
              <Plus className="w-4 h-4 mr-2" />İlk Paketi Ekle
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminCorporateServerPackages;


